import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/* Lyphéa — cart drawer content (rendered from /cart.js) */
const TAG = '<svg viewBox="0 0 12 12" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M7 0h3a2 2 0 012 2v3a1 1 0 01-.3.7l-6 6a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4l6-6A1 1 0 017 0zm2 2a1 1 0 100 2 1 1 0 000-2z" fill="currentColor"/></svg>';
const TRASH = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 4h12M6 4V2.7c0-.4.3-.7.7-.7h2.6c.4 0 .7.3.7.7V4m2 0v8.7c0 .7-.6 1.3-1.3 1.3H5.3C4.6 14 4 13.4 4 12.7V4m2.7 3v4m2.6-4v4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ARROW = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 2v8h9m-3-3 3 3-3 3" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const TRUCK = '<svg width="16" height="14" viewBox="0 0 16 14" fill="none" aria-hidden="true"><path d="M1 2.5h9v7H1zM10 5h3l2 2.5v2H10z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><circle cx="4" cy="11" r="1.6" fill="#fff" stroke="currentColor" stroke-width="1.3"/><circle cx="12" cy="11" r="1.6" fill="#fff" stroke="currentColor" stroke-width="1.3"/></svg>';

const fmt = (c) => { const n = c < 0; c = Math.abs(Math.round(c)); return (n ? '-' : '') + '$' + Math.floor(c / 100) + '.' + String(c % 100).padStart(2, '0'); };
const fmtShort = (c) => (Math.round(c) % 100 === 0 ? '$' + Math.round(c) / 100 : fmt(c));
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]));
const img = (u, w = 160) => (u ? u + (u.includes('?') ? '&' : '?') + 'width=' + w : '');

class LypCart extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    try { this.cfg = JSON.parse(this.dataset.config || '{}'); } catch { this.cfg = {}; }
    this.body = this.querySelector('[data-lyp-body]');
    this.footer = this.querySelector('[data-lyp-footer]');
    this.drawer = document.getElementById('cart-drawer');
    this.querySelector('[data-lyp-close]')?.addEventListener('click', () => this.drawer?.close?.());
    this.addEventListener('click', (e) => this.onClick(e));
    this.addEventListener('change', (e) => this.onChange(e));
    document.addEventListener(StandardEvents.cartLinesUpdate, (ev) => {
      if (this._busy) return;
      ev.promise?.then(() => this.refresh()).catch(() => {});
    });
    document.addEventListener('click', (e) => {
      const d = this.drawer; if (!d || !d.isOpen) return;
      if (e.target.closest('dialog') || e.target.closest('.header-actions__cart-icon, cart-icon') || e.target.closest('[data-lyp-stay]')) return;
      if (document.documentElement.matches(':has(.page-wrapper--drawer-open)')) d.close?.();
    });
    window.addEventListener('pageshow', (e) => { if (e.persisted) this.refresh(); });
    this.refresh();
  }

  async refresh() {
    try { this.cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()); } catch { return; }
    this.render();
  }

  isIns(i) { return !!(this.cfg.insurance && i.product_id === this.cfg.insurance.id); }

  isGift(i) { return i.properties && (i.properties._gift === 'true' || i.properties._gift === true); }

  render(keep = false) {
    const cart = this.cart;
    const scroller = this.querySelector('.lyp-cd__contents');
    const prevTop = keep && scroller ? scroller.scrollTop : 0;
    const items = cart.items.map((it, idx) => ({ ...it, line: idx + 1 }));
    const gifts = items.filter((i) => this.isGift(i));
    const insLine = items.find((i) => this.isIns(i));
    const mains = items.filter((i) => !this.isGift(i) && !this.isIns(i)).sort((a, b) => (b.properties && b.properties.__bundle ? 1 : 0) - (a.properties && a.properties.__bundle ? 1 : 0));
    this.querySelector('[data-lyp-count]').textContent = cart.item_count;
    this.querySelector('[data-lyp-count-wrap]').hidden = cart.item_count === 0;
    this.closest('.cart-drawer__inner')?.classList.toggle('cart-drawer--empty', cart.item_count === 0);

    if (!items.filter((i) => !this.isIns(i)).length) {
      this.body.innerHTML = '<div class="lyp-cd__empty"><p>Your cart is empty</p><a class="lyp-cd__btn" href="/collections/all">Continue shopping</a></div>';
      this.footer.hidden = true;
      return;
    }

    const parts = [];
    // reservation banner
    if (!this.cfg.banner) this.cfg.banner = 'Your order is reserved for {time} ⏰';
    if (this.cfg.banner && this.cfg.timer) parts.push('<div class="lyp-cd__banner" data-lyp-banner></div>');
    // free shipping bar
    const th = this.cfg.threshold || 0;
    if (th) {
      const pct = Math.min(100, (cart.total_price / th) * 100);
      const left = th - cart.total_price;
      const txt = left > 0 ? `Only <strong>${fmt(left)}</strong> away from <strong>free shipping</strong>` : '<strong>Free shipping</strong> unlocked';
      parts.push(`<div class="lyp-cd__tier"><p class="lyp-cd__tier-text">${txt}</p><div class="lyp-cd__bar"><div class="lyp-cd__bar-fill" style="width:${pct}%"></div><div class="lyp-cd__bar-icon"><svg viewBox="0 0 22 22" fill="none"><circle cx="11" cy="11" r="10" fill="#1b462c" stroke="#1b462c" stroke-width="2"/><circle cx="11" cy="11" r="6" fill="#1b462c" stroke="#fff" stroke-width="2"/></svg><div class="lyp-cd__tip">${TRUCK}<span>${left > 0 ? 'Free shipping' : '✅ Free shipping'}</span></div></div></div></div>`);
    }
    // items
    const itemHtml = [];
    let giftsPlaced = false;
    mains.forEach((it, n) => {
      itemHtml.push(this.mainItem(it));
      const isBundle = it.properties && it.properties.__bundle;
      if (!giftsPlaced && gifts.length && (isBundle || n === mains.length - 1)) { itemHtml.push(this.giftBlock(gifts)); giftsPlaced = true; }
    });
    if (!mains.length && gifts.length) itemHtml.push(this.giftBlock(gifts));
    parts.push(`<div class="lyp-cd__items">${itemHtml.join('')}</div>`);
    // upsell
    const up = this.cfg.upsell;
    if (up && up.available && !cart.items.some((i) => i.product_id === up.id)) {
      const old = up.compare > up.price ? `<s>${fmt(up.compare)}</s><span class="lyp-cd__badge lyp-cd__badge--plain">You save ${Math.round((1 - up.price / up.compare) * 100)}%</span>` : '';
      parts.push(`<div class="lyp-cd__upsell"><p class="lyp-cd__upsell-title">You may also like:</p><div class="lyp-cd__upsell-list"><div class="lyp-cd__upsell-item"><a href="${esc(up.url)}" class="lyp-cd__upsell-img"><img src="${esc(up.image)}" alt="" width="60" height="60" loading="lazy"></a><div class="lyp-cd__upsell-content"><a class="lyp-cd__upsell-name" href="${esc(up.url)}">${esc(up.title)}</a><div class="lyp-cd__upsell-price"><b>${fmt(up.price)}</b>${old}</div><div><button type="button" class="lyp-cd__upsell-add" data-lyp-upsell="${up.variant}">Add to cart</button></div></div></div></div></div>`);
    }
    this.body.innerHTML = parts.join('');
    if (scroller) scroller.scrollTop = prevTop;

    // footer
    this.renderInsurance(!!insLine);
    const giftCompare = items.reduce((s, g) => { const c = (parseInt(g.properties && g.properties._compare, 10) || 0) * g.quantity; return s + Math.max(0, c - g.original_line_price); }, 0);
    const orig = cart.original_total_price + giftCompare;
    const promo = orig - cart.total_price;
    const titles = [...new Set(items.flatMap((i) => (i.line_level_discount_allocations || []).map((a) => a.discount_application.title)))];
    const d = this.querySelector('[data-lyp-discounts]');
    d.innerHTML = promo > 0 ? `<div class="lyp-cd__disc-title">Promotion ${titles.map((t) => `<span class="lyp-cd__disc-badge"><svg viewBox="0 0 16 16" fill="none"><path d="M8.6 1.5H13a1.5 1.5 0 0 1 1.5 1.5v4.4a1 1 0 0 1-.3.7l-6 6a1 1 0 0 1-1.4 0L2.4 10.2a1 1 0 0 1 0-1.4l6-6a1 1 0 0 1 .2-.1z" stroke="currentColor" stroke-width="1.2"/><circle cx="11.2" cy="4.8" r="1" fill="currentColor"/></svg>${esc(t)}</span>`).join('')}</div><div class="lyp-cd__disc-amount">${fmt(-promo)}</div>` : '';
    d.hidden = promo <= 0;
    this.querySelector('[data-lyp-subtotal]').innerHTML = (promo > 0 ? `<s>${fmt(orig)}</s>` : '') + fmt(cart.total_price);
    this.querySelector('[data-lyp-checkout]').textContent = 'Confirm my order • ' + fmt(cart.total_price);
    this.footer.hidden = false;
    this.startTimer();
  }

  discountBadge(i) {
    const t = (i.line_level_discount_allocations || [])[0]?.discount_application.title;
    return t ? `<div class="lyp-cd__badge lyp-cd__badge--bundle"><i>${TAG}</i><span>${esc(t)}</span></div>` : '';
  }

  qty(i, small) {
    return `<div class="lyp-cd__qty"><button type="button" data-lyp-dec="${i.line}" aria-label="−">−</button><input type="text" inputmode="numeric" value="${i.quantity}" data-lyp-q="${i.line}" aria-label="Quantity"><button type="button" data-lyp-inc="${i.line}" aria-label="+">+</button></div>`;
  }

  mainItem(i) {
    const title = esc(i.product_title) + (i.variant_title && i.variant_title !== 'Default Title' && i.variant_title !== 'Standard' && i.variant_title !== 'Promo' && i.variant_title !== 'Gift' ? ' — ' + esc(i.variant_title) : '');
    const prefix = i.quantity > 1 && i.properties && i.properties.__bundle ? `<span class="lyp-cd__bq">${i.quantity}x</span> ` : '';
    const cmp = (parseInt(i.properties && i.properties._compare, 10) || 0) * i.quantity;
    const base = Math.max(i.original_line_price, cmp);
    const saved = base - i.final_line_price;
    const price = `<div class="lyp-cd__total">${saved > 0 ? `<s>${fmt(base)}</s>` : ''}<b>${fmt(i.final_line_price)}</b></div>`;
    const pill = saved > 0 ? `<div class="lyp-cd__badge"><i>${TAG}</i><span>You save ${fmtShort(saved)}</span></div>` : '';
    return `<div class="lyp-cd__item"><a class="lyp-cd__img" href="${esc(i.url)}"><img src="${esc(img(i.image))}" alt="" width="80" height="80"></a><div class="lyp-cd__content"><div class="lyp-cd__left"><div><a class="lyp-cd__name" href="${esc(i.url)}">${prefix}${title}</a>${this.discountBadge(i)}</div>${this.qty(i)}</div><div class="lyp-cd__right"><div class="lyp-cd__price">${price}${pill}</div><button type="button" class="lyp-cd__remove" data-lyp-rm="${i.line}" aria-label="Remove">${TRASH}</button></div></div></div>`;
  }

  giftBlock(gifts) {
    return '<div class="lyp-cd__extras">' + gifts.map((g) => {
      const cmp = (parseInt(g.properties._compare, 10) || 0) * g.quantity;
      const free = g.final_line_price === 0;
      return `<div class="lyp-cd__item lyp-cd__item--extra"><div class="lyp-cd__img lyp-cd__img--extra"><span class="lyp-cd__arrow">${ARROW}</span><a href="${esc(g.url)}"><img src="${esc(img(g.image))}" alt="" width="50" height="50"></a></div><div class="lyp-cd__content"><div class="lyp-cd__left"><div><a class="lyp-cd__name lyp-cd__name--sm" href="${esc(g.url)}">${esc(g.product_title)}</a></div>${this.qty(g)}</div><div class="lyp-cd__right"><div class="lyp-cd__price"><div class="lyp-cd__total lyp-cd__total--sm">${cmp > g.final_line_price ? `<s>${fmt(cmp)}</s>` : ''}<b>${free ? 'FREE' : fmt(g.final_line_price)}</b></div>${free && cmp ? `<div class="lyp-cd__badge"><i>${TAG}</i><span>You save 100%</span></div>` : ''}</div><button type="button" class="lyp-cd__remove" data-lyp-rm="${g.line}" aria-label="Remove">${TRASH}</button></div></div></div>`;
    }).join('') + '</div>';
  }

  renderInsurance(on) {
    const box = this.querySelector('[data-lyp-ins]');
    const ins = this.cfg.insurance;
    if (!box || !ins) { if (box) box.hidden = true; return; }
    box.hidden = false;
    box.innerHTML = `<div class="lyp-cd__ins-img"><img src="${esc(ins.image)}" alt="" width="52" height="60"></div><div class="lyp-cd__ins-body"><div class="lyp-cd__ins-top"><span class="lyp-cd__ins-title">${esc(ins.title)}</span><span class="lyp-cd__ins-price"><b>${fmt(ins.price)}</b><button type="button" class="lyp-cd__toggle${on ? ' is-on' : ''}" role="switch" aria-checked="${on}" aria-label="${esc(ins.title)}" data-lyp-toggle><i></i></button></span></div><span class="lyp-cd__ins-desc">${esc(ins.desc)}</span></div>`;
  }

  async toggleInsurance() {
    const ins = this.cfg.insurance;
    if (!ins) return;
    const line = this.cart.items.findIndex((i) => this.isIns(i)) + 1;
    if (line) return this.change(line, 0);
    this._busy = true;
    try {
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'cart', lines: [{ merchandiseId: String(ins.variant), quantity: 1 }], promise: deferred.promise }));
      await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(ins.variant), quantity: 1 }] }) });
      const cart = await fetch('/cart.js').then((r) => r.json());
      this.cart = cart; this.render(true);
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { itemCount: cart.item_count, items: cart.items, didError: false } });
    } finally { this._busy = false; }
  }

  startTimer() {
    const el = this.querySelector('[data-lyp-banner]');
    if (!el) return;
    const KEY = 'lypCartTimerEnd';
    let end = 0;
    try { end = parseInt(sessionStorage.getItem(KEY) || '0', 10); } catch {}
    if (!end || end < Date.now() - 1000) { end = Date.now() + (this.cfg.timer || 10) * 60000; try { sessionStorage.setItem(KEY, String(end)); } catch {} }
    clearInterval(this._t);
    const tick = () => {
      const left = Math.max(0, Math.round((end - Date.now()) / 1000));
      const t = String(Math.floor(left / 60)).padStart(2, '0') + ':' + String(left % 60).padStart(2, '0');
      el.innerHTML = esc(this.cfg.banner).replace('{time}', `<strong>${t}</strong>`);
      if (!left) clearInterval(this._t);
    };
    tick();
    this._t = setInterval(tick, 1000);
  }

  async change(line, quantity) {
    this._busy = true;
    try {
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({ action: quantity === 0 ? 'remove' : 'update', context: 'cart', lines: [], promise: deferred.promise }));
      const res = await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity }) });
      let cart = await res.json();
      // drop orphan gifts when no paid item is left
      if (cart.items && cart.items.length && cart.items.every((i) => this.isGift(i) || this.isIns(i))) {
        cart = await fetch('/cart/clear.js', { method: 'POST' }).then((r) => r.json());
      }
      this.cart = cart;
      this.render(true);
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { itemCount: cart.item_count, items: cart.items, didError: false } });
    } catch (e) { console.error(e); } finally { setTimeout(() => { this._busy = false; }, 50); }
  }

  onClick(e) {
    const t = e.target.closest('button');
    if (!t) return;
    if (t.dataset.lypToggle !== undefined) return this.toggleInsurance();
    const line = (a) => parseInt(t.dataset[a], 10);
    const get = (l) => this.cart.items[l - 1];
    if (t.dataset.lypDec) { const l = line('lypDec'); this.change(l, Math.max(0, get(l).quantity - 1)); }
    else if (t.dataset.lypInc) { const l = line('lypInc'); this.change(l, get(l).quantity + 1); }
    else if (t.dataset.lypRm) this.change(line('lypRm'), 0);
    else if (t.dataset.lypUpsell) this.addUpsell(t);
  }

  onChange(e) {
    const i = e.target.closest('input[data-lyp-q]');
    if (!i) return;
    const q = Math.max(0, parseInt(i.value, 10) || 0);
    this.change(parseInt(i.dataset.lypQ, 10), q);
  }

  async addUpsell(btn) {
    btn.disabled = true;
    this._busy = true;
    try {
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'cart', lines: [{ merchandiseId: btn.dataset.lypUpsell, quantity: 1 }], promise: deferred.promise }));
      await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(btn.dataset.lypUpsell), quantity: 1, properties: { _upsell: 'true', _compare: String(this.cfg.upsell.compare || 0) } }] }) });
      const cart = await fetch('/cart.js').then((r) => r.json());
      this.cart = cart; this.render();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { itemCount: cart.item_count, items: cart.items, didError: false } });
    } finally { this._busy = false; }
  }
}
if (!customElements.get('lyphea-cart')) customElements.define('lyphea-cart', LypCart);
